import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import _ from 'lodash';
import {
  Grid,
  Chip,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from '@mui/material';
import { AutocompleteV, SimpleDialog, Icons } from 'components';
import Services from './services/Services';
import { useStyles } from './style';
import { formatPrice } from 'utility';

const Accounts = (props) => {
  // --PROPS--//
  const {
    methods,
    singleSelect = false,
    selected = [],
    setSelected = () => {},
    className,
    style,
    showBalance = true,
    disableNoBalances = false,
    placeholder = 'جستجو و انتخاب کنید...',
    label = 'حساب‌های تفصیلی کسب‌وکار',
    name = 'guilds',
    serviceName,
    currencyText,
    ...rest
  } = props;

  // --STATES--//
  const [isFullListModalOpen, setIsFullListModalOpen] = useState(false);
  const [guildSearchText, setGuildSearchText] = useState('');
  const [guildsList, setGuildsList] = useState([]);
  const [guildListLoading, setGuildListLoading] = useState(false);

  // --HOOKS--//
  const classes = useStyles();

  useEffect(() => {
    // --API Requests--//
    const getGuildsList = async (val = '') => {
      setGuildListLoading(true);

      try {
        const q = {};
        if (!serviceName) {
          const result = await Services.getCreditAccounts(q);
          const data = [...result.result.mainBusinessAmountSrvs];
          if (data.length > 0) {
            let list = [];
            list = data.map((val) => ({
              name:
                (val?.aliasName ?? val?.guildSrv?.name) +
                (showBalance
                  ? ` (قابل برداشت: ${
                      val?.withdrawableAmount != null
                        ? formatPrice(val?.withdrawableAmount, { currency: currencyText })
                        : '-'
                    })`
                  : ''),
              id: val.id,
              label:
                (val?.aliasName ?? val?.guildSrv?.name) +
                (showBalance
                  ? ` (قابل برداشت: ${
                      val?.withdrawableAmount != null
                        ? formatPrice(val?.withdrawableAmount, { currency: currencyText })
                        : '-'
                    })`
                  : ''),
              value: val.id,
              code: val?.guildSrv?.code,
              currency: val?.currencySrv,
              balance: val?.withdrawableAmount ?? 0,
            }));
            setGuildsList(list);
          }
        } else {
          const result = await Services.getGuilds();
          const data = [...result.result.guilds];
          if (data.length > 0) {
            let list = [];
            list = data.map((val) => ({
              id: val.id,
              label: val.name,
              value: val.code,
            }));
            setGuildsList(list);
          }
        }
      } catch (e) {}
      setGuildListLoading(false);
    };

    getGuildsList();
  }, [showBalance]);

  // -- OTHER FUNCTIONS --//
  const handleDeselectItem = (id, arr, setter) => {
    const temp = [...arr];
    _.remove(temp, function (v) {
      return v?.id === id;
    });
    setter([...temp]);
  };

  const showFullModal = () => {
    setIsFullListModalOpen(true);
  };

  const getCurrentFullList = () => {
    return singleSelect ? selected : [...selected];
  };

  const getCurrentFullListSetter = () => {
    return setSelected;
  };

  const hideFullModal = () => {
    setIsFullListModalOpen(false);
  };

  return (
    <Grid item xs={12}>
      <Grid item xs={12} className={clsx(classes.root, className)} style={style}>
        <AutocompleteV
          control={methods?.control}
          errors={methods?.errors}
          register={methods?.register}
          placeholder={placeholder}
          name={name}
          label={label}
          list={guildsList}
          loading={guildListLoading}
          inputValue={singleSelect ? undefined : guildSearchText}
          getOptionSelected={(option, value) => option.id === value.id}
          getOptionDisabled={(option) =>
            (singleSelect
              ? selected?.value === option?.value
              : selected?.find((v) => v?.id === option?.id) != null) ||
            (option?.balance === 0 && disableNoBalances)
          }
          onChangeValue={(p) => {
            setGuildSearchText('');
            if (!p) return;
            if (singleSelect) {
              setSelected(p);
            } else {
              const temp = [...selected];
              temp.push(p);
              setSelected([...temp]);
            }
            if (methods?.setValue) {
              methods?.setValue(name, p);
            }
          }}
          preventAddCustomOption
          clearOn={!singleSelect}
          disableClearable={!singleSelect}
          {...rest}
        />

        {!singleSelect ? (
          <div style={{ marginTop: 0 }}>
            {selected.map((v, k) =>
              k > 4 ? null : (
                <Chip
                  className={classes.chip}
                  key={k}
                  label={<Typography style={{ fontSize: 12 }}>{`${v?.id}. ${v?.name}`}</Typography>}
                  onClick={() => {}}
                  onDelete={() => handleDeselectItem(v?.id, selected, setSelected)}
                />
              ),
            )}
            {selected.length > 4 ? (
              <Chip
                className={classes.chipOutline}
                label={<Typography style={{ fontSize: 12 }}>نمایش بیشتر...</Typography>}
                onClick={() => showFullModal()}
              />
            ) : null}
          </div>
        ) : null}
      </Grid>

      {/* Selected Items List */}
      {!singleSelect ? (
        <SimpleDialog
          title="لیست حساب‌های انتخاب شده"
          open={isFullListModalOpen}
          onClose={() => hideFullModal()}
        >
          <TableContainer elevation={0} component={Paper} className={classes.table}>
            <Table>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell className={classes.tableCell} style={{ width: '10%' }}>
                    شناسه
                  </TableCell>
                  <TableCell className={classes.tableCell}>عنوان</TableCell>
                  <TableCell className={classes.tableCell} />
                </TableRow>
              </TableHead>
              <TableBody>
                {/* List Data Map */}
                {getCurrentFullList().map((v, k) => (
                  <TableRow key={k}>
                    <TableCell className={classes.tableDataCell}>{v?.id}</TableCell>
                    <TableCell className={classes.tableDataCell}>{v?.label}</TableCell>
                    <TableCell align="right" className={classes.tableDataCell}>
                      <IconButton
                        className={classes.actionsIcon}
                        onClick={() => {
                          handleDeselectItem(
                            v?.id,
                            getCurrentFullList(),
                            getCurrentFullListSetter(),
                          );
                        }}
                        size="large">
                        <Icons icon="Trash" color="#2b3990" size="20" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SimpleDialog>
      ) : null}
      {/* End Selected Items List */}
    </Grid>
  );
};

export default Accounts;
